import { SelectHTMLAttributes, forwardRef } from 'react'
import { ChevronDown } from 'lucide-react'

export interface SelectOption {
  value: string
  label: string
  description?: string
  disabled?: boolean
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  options: SelectOption[]
  error?: string
  helperText?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      options,
      error,
      helperText,
      className = '',
      ...props
    },
    ref
  ) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-dark-text-secondary mb-2">
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            className={`
              w-full px-4 py-2.5 pr-10 rounded-lg appearance-none
              bg-dark-bg-secondary border border-dark-border
              text-dark-text-primary
              focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500
              transition-all duration-200
              ${error ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' : ''}
              ${className}
            `}
            {...props}
          >
            {options.map((option) => (
              <option 
                key={option.value} 
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-text-muted pointer-events-none" />
        </div>
        {(error || helperText) && (
          <p className={`text-sm mt-1.5 ${error ? 'text-red-400' : 'text-dark-text-muted'}`}>
            {error || helperText}
          </p>
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'

// Style Selector Component
interface StyleSelectorProps {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  label?: string
}

export const StyleSelector = ({ value, onChange, options, label }: StyleSelectorProps) => {
  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-medium text-dark-text-secondary">
          {label}
        </label>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={`
              relative p-4 rounded-lg border text-left transition-all duration-200
              ${value === option.value
                ? 'bg-brand-500/10 border-brand-500 shadow-lg shadow-brand-500/10'
                : 'bg-dark-bg-secondary border-dark-border hover:border-dark-text-muted'
              }
            `}
          >
            <div className="font-medium text-dark-text-primary">
              {option.label}
            </div>
            {option.description && (
              <div className="text-xs text-dark-text-muted mt-1">
                {option.description}
              </div>
            )}
            {value === option.value && (
              <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-brand-500" />
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
